import {Icon, LatLngExpression} from 'leaflet';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Marker, Popup} from 'react-leaflet';
import {useMapDataContext} from 'src/context/MapDataContext';
import {UiMarkerActions, UiStringActions, useUiContext} from 'src/context/UiContext';
import {Location} from 'src/services/api/types/types';
import {LocationPopup} from '../location-popup/LocationPopup';

interface LocationMarkerProps {
    position: LatLngExpression;
    location: Location;
    zoomLevel: number;
}

export function LocationMarker({position, location, zoomLevel}: LocationMarkerProps) {
    const {t} = useTranslation();
    const {state, dispatch} = useUiContext();

    const hoverActive =
        (state.hoveredClinicId?.type === 'LOCATION' && state.hoveredClinicId.id === location.id) ||
        (state.stickyClinicId?.type === 'LOCATION' && state.stickyClinicId.id === location.id);

    const extraSize = hoverActive ? 6 : 0;
    const zIndexOffset = hoverActive ? 1000 : 0;
    const baseIconSize = 2 + zoomLevel * 2.5 + extraSize;
    const baseShadowSize = 2 * 1.22 + zoomLevel * 2.5 + extraSize;
    const baseAnchor = 1 + extraSize / 2 + (zoomLevel * 2.5) / 2;

    const {getDistanceToLocation} = useMapDataContext();
    const locationPopupToRender = useMemo(
        () => (
            <LocationPopup
                location={location}
                isOpenId={state.popupOpenId?.type === 'LOCATION' && state.popupOpenId.id === location.id}
                getDistanceToLocation={getDistanceToLocation(location)}
            />
        ),
        [location, getDistanceToLocation, state.popupOpenId]
    );
    return (
        <Marker
            alt={t(`map_position_marker.text`) as string}
            position={position}
            zIndexOffset={zIndexOffset}
            icon={
                new Icon({
                    className: `clinic_map_marker${hoverActive ? ' clinic_map_marker--hovered' : ''}`,
                    iconUrl: hoverActive ? '/map_pin_bus_dark.png' : '/map_pin_bus.png',
                    shadowUrl: '/map_pin_small_shadow@2x.png',
                    iconSize: [baseIconSize, baseIconSize * 1.166],
                    shadowSize: [baseShadowSize, baseShadowSize * 1.159],
                    iconAnchor: [baseAnchor, baseIconSize + extraSize],
                    shadowAnchor: [baseAnchor, baseShadowSize + extraSize - (zoomLevel < 9.2 ? 0 : zoomLevel / 4)],
                    popupAnchor: [0, -32]
                })
            }
            eventHandlers={{
                popupopen: () =>
                    dispatch({type: UiMarkerActions.SET_POPUP_OPEN_ID, value: {type: 'LOCATION', id: location.id}}),
                popupclose: () => dispatch({type: UiMarkerActions.SET_POPUP_OPEN_ID, value: null})
            }}
        >
            {locationPopupToRender}
        </Marker>
    );
}
